import { qLocal, qLocalAll, qDirectChild } from "./xmlUtils";

const CHART_URI    = "http://schemas.openxmlformats.org/drawingml/2006/chart";
const CHART_EX_URI = "http://schemas.microsoft.com/office/drawing/2014/chartex";
const ANCHOR_NAMES = ["twoCellAnchor", "oneCellAnchor", "absoluteAnchor"];
const STD_CHART_TYPES = [
  "barChart", "bar3DChart", "lineChart", "line3DChart",
  "pieChart", "pie3DChart", "doughnutChart", "areaChart", "area3DChart",
  "scatterChart", "radarChart", "bubbleChart", "stockChart", "surfaceChart",
  "surface3DChart", "ofPieChart",
];

const CHART_EX_LAYOUT_MAP = {
  treemap:         "treemap",
  sunburst:        "sunburst",
  funnel:          "funnel",
  waterfall:       "waterfall",
  boxWhisker:      "boxWhisker",
  regionMap:       "bar",
  clusteredColumn: "bar",
  paretoLine:      "line",
  histogram:       "histogram",
};

const RENDER_TYPE_MAP = {
  barChart: "bar", bar3DChart: "bar",
  lineChart: "line", line3DChart: "line",
  pieChart: "pie", pie3DChart: "pie",
  doughnutChart: "doughnut",
  areaChart: "area", area3DChart: "area",
  scatterChart: "scatter",
  radarChart: "radar",
  bubbleChart: "bubble",
  stockChart: "stock",
  surfaceChart: "surface", surface3DChart: "surface",
  ofPieChart: "pie",
};

function resolveZipPath(basePath, target) {
  if (!target) return null;
  if (target.startsWith("/")) return target.replace(/^\//, "");
  const baseDir = basePath.replace(/\/[^/]+$/, "");
  const baseRoot = baseDir.replace(/\/_rels$/, "");
  const segments = baseRoot.split("/");
  const rest = target.split("/");
  for (const seg of rest) {
    if (seg === "..") segments.pop();
    else if (seg !== ".") segments.push(seg);
  }
  return segments.join("/");
}

function parseCellRef(ref) {
  if (!ref) return null;
  const m = ref.match(/^\$?([A-Z]+)\$?(\d+)$/i);
  if (!m) return null;
  const letters = m[1].toUpperCase();
  let col = 0;
  for (let i = 0; i < letters.length; i++) col = col * 26 + (letters.charCodeAt(i) - 64);
  return { row: parseInt(m[2], 10) - 1, col: col - 1 };
}

function parseSheetRangeFormula(formula) {
  if (!formula) return null;
  const trimmed = formula.trim().replace(/^=/, "");
  const m = trimmed.match(/^(?:'([^']+)'|([^!]+))!(.+)$/);
  if (!m) return null;
  const sheet = m[1] || m[2];
  const rangePart = m[3];
  const [tlRef, brRef] = rangePart.split(":");
  const tl = parseCellRef(tlRef);
  const br = brRef ? parseCellRef(brRef) : tl;
  if (!tl) return null;
  return { sheet, tl, br: br || tl };
}

async function loadWorkbookContext(zip) {
  const ctx = {
    sheetIdToName: {},
    sheetPathToName: {},
    sheetNameToPath: {},
    definedNames: {},
    sharedStrings: [],
    sheetCellCache: {},
  };

  const wbXmlFile = zip.file("xl/workbook.xml");
  if (!wbXmlFile) return ctx;
  const wbXml = await wbXmlFile.async("string");
  const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");

  qLocalAll(wbDoc, "sheet").forEach(node => {
    const rId = node.getAttribute("r:id") ||
                node.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id");
    const name = node.getAttribute("name");
    if (rId && name) ctx.sheetIdToName[rId] = name;
  });

  qLocalAll(wbDoc, "definedName").forEach(node => {
    const name = node.getAttribute("name");
    const text = (node.textContent || "").trim();
    if (name) ctx.definedNames[name] = text;
  });

  const wbRelsFile = zip.file("xl/_rels/workbook.xml.rels");
  if (wbRelsFile) {
    const wbRelsXml = await wbRelsFile.async("string");
    const wbRelsDoc = new DOMParser().parseFromString(wbRelsXml, "application/xml");
    qLocalAll(wbRelsDoc, "Relationship").forEach(rel => {
      const id = rel.getAttribute("Id");
      const target = rel.getAttribute("Target");
      if (!id || !target) return;
      if (ctx.sheetIdToName[id]) {
        const cleanTarget = target.replace(/^\/?xl\//, "");
        ctx.sheetPathToName[cleanTarget] = ctx.sheetIdToName[id];
        ctx.sheetNameToPath[ctx.sheetIdToName[id]] = "xl/" + cleanTarget;
      }
    });
  }

  const ssFile = zip.file("xl/sharedStrings.xml");
  if (ssFile) {
    const ssXml = await ssFile.async("string");
    const ssDoc = new DOMParser().parseFromString(ssXml, "application/xml");
    qLocalAll(ssDoc, "si").forEach(si => {
      const tNodes = qLocalAll(si, "t");
      const text = tNodes.map(t => t.textContent || "").join("");
      ctx.sharedStrings.push(text);
    });
  }

  return ctx;
}

async function getSheetCellMap(zip, ctx, sheetPath) {
  if (!sheetPath) return new Map();
  if (ctx.sheetCellCache[sheetPath]) return ctx.sheetCellCache[sheetPath];

  const file = zip.file(sheetPath);
  if (!file) { ctx.sheetCellCache[sheetPath] = new Map(); return ctx.sheetCellCache[sheetPath]; }

  const xml = await file.async("string");
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const map = new Map();

  qLocalAll(doc, "row").forEach(rowEl => {
    qLocalAll(rowEl, "c").forEach(cEl => {
      const ref = cEl.getAttribute("r");
      if (!ref) return;
      const pos = parseCellRef(ref);
      if (!pos) return;
      const t = cEl.getAttribute("t") || "n";
      let value = "";

      if (t === "inlineStr") {
        const isNode = qLocal(cEl, "is");
        if (isNode) {
          const tNodes = qLocalAll(isNode, "t");
          value = tNodes.map(n => n.textContent || "").join("");
        }
      } else {
        const vNode = qLocal(cEl, "v");
        const raw = vNode ? (vNode.textContent || "") : "";
        if (t === "s") {
          const idx = parseInt(raw, 10);
          value = (Number.isFinite(idx) && ctx.sharedStrings[idx] != null) ? ctx.sharedStrings[idx] : "";
        } else {
          value = raw;
        }
      }
      map.set(pos.row + ":" + pos.col, value);
    });
  });

  ctx.sheetCellCache[sheetPath] = map;
  return map;
}

async function readRangeValues(zip, ctx, formulaOrName) {
  if (!formulaOrName) return [];
  let formula = formulaOrName.trim();
  if (ctx.definedNames[formula]) formula = ctx.definedNames[formula];

  const parsed = parseSheetRangeFormula(formula);
  if (!parsed) return [];
  const sheetPath = ctx.sheetNameToPath[parsed.sheet];
  if (!sheetPath) return [];
  const cells = await getSheetCellMap(zip, ctx, sheetPath);

  const values = [];
  const tl = parsed.tl;
  const br = parsed.br;
  for (let r = tl.row; r <= br.row; r++) {
    for (let c = tl.col; c <= br.col; c++) {
      values.push(cells.get(r + ":" + c) || "");
    }
  }
  return values;
}

function readPosNode(node) {
  if (!node) return null;
  return {
    col:    parseInt(qLocal(node, "col")?.textContent    || "0", 10),
    row:    parseInt(qLocal(node, "row")?.textContent    || "0", 10),
    colOff: parseInt(qLocal(node, "colOff")?.textContent || "0", 10),
    rowOff: parseInt(qLocal(node, "rowOff")?.textContent || "0", 10),
  };
}

function readExtNode(node) {
  if (!node) return null;
  const cx = parseInt(node.getAttribute("cx") || "0", 10);
  const cy = parseInt(node.getAttribute("cy") || "0", 10);
  if (!cx && !cy) return null;
  return { cx, cy };
}

function findChartRefInGraphicFrame(graphicFrame) {
  if (!graphicFrame) return null;
  const graphicData = qLocal(graphicFrame, "graphicData");
  if (!graphicData) return null;
  const uri = (graphicData.getAttribute("uri") || "").trim();

  const chartNode = qLocal(graphicData, "chart");
  if (!chartNode) return null;
  const rId = chartNode.getAttributeNS(
    "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
    "id"
  ) || chartNode.getAttribute("r:id");
  if (!rId) return null;

  const isExtended = uri === CHART_EX_URI ||
                     chartNode.namespaceURI === CHART_EX_URI ||
                     uri.indexOf("chartex") !== -1;
  return { rId: rId, isExtended: isExtended };
}

function extractAnchorChart(anchor) {
  let graphicFrame = qDirectChild(anchor, "graphicFrame") || qLocal(anchor, "graphicFrame");

  if (!graphicFrame || !findChartRefInGraphicFrame(graphicFrame)) {
    const altContents = qLocalAll(anchor, "AlternateContent");
    for (const alt of altContents) {
      const choice = qLocal(alt, "Choice");
      if (choice) {
        const gf = qLocal(choice, "graphicFrame");
        if (gf && findChartRefInGraphicFrame(gf)) { graphicFrame = gf; break; }
      }
    }
  }

  const ref = findChartRefInGraphicFrame(graphicFrame);
  if (!ref) return null;

  const fromNode = qLocal(anchor, "from");
  const toNode   = qLocal(anchor, "to");
  const extNode  = qDirectChild(anchor, "ext") || qLocal(anchor, "ext");

  const tl  = readPosNode(fromNode) || { col: 0, row: 0, colOff: 0, rowOff: 0 };
  const br  = toNode ? readPosNode(toNode) : null;
  const ext = readExtNode(extNode);

  return { ref: ref, range: { tl: tl, br: br, ext: ext } };
}

function parseStandardChart(chartDoc) {
  const result = { title: "", chartType: "bar", isCombo: false, series: [], categories: [] };

  const chartElem = qLocal(chartDoc, "chart");
  const titleNode = chartElem ? qDirectChild(chartElem, "title") : qLocal(chartDoc, "title");
  if (titleNode) {
    const txNode = qLocal(titleNode, "tx");
    if (txNode) {
      const richNode = qLocal(txNode, "rich");
      if (richNode) {
        const tNodes = qLocalAll(richNode, "t");
        if (tNodes.length > 0) result.title = tNodes.map(t => t.textContent).join("").trim();
      }
      if (!result.title) {
        const strCache = qLocal(txNode, "strCache");
        if (strCache) {
          const vNode = qLocal(strCache, "v");
          if (vNode) result.title = vNode.textContent.trim();
        }
      }
    }
    if (!result.title) {
      const fallbackTexts = qLocalAll(titleNode, "t");
      if (fallbackTexts.length > 0) result.title = fallbackTexts.map(t => t.textContent).join("").trim();
    }
  }
  if (!result.title) result.title = "Chart";

  const plotArea = qLocal(chartDoc, "plotArea");
  if (!plotArea) return result;

  const chartGroups = [];
  for (const typeName of STD_CHART_TYPES) {
    const found = qLocalAll(plotArea, typeName);
    found.forEach(el => {
      const renderType = RENDER_TYPE_MAP[typeName] || typeName.replace("3DChart", "").replace("Chart", "");
      chartGroups.push({ element: el, typeName: typeName, renderType: renderType });
    });
  }
  if (chartGroups.length === 0) return result;

  result.chartType = chartGroups[0].renderType;
  const uniqueTypes = Array.from(new Set(chartGroups.map(g => g.renderType)));
  result.isCombo = uniqueTypes.length > 1;

  const allSerNodes = [];
  for (const group of chartGroups) {
    const serNodes = qLocalAll(group.element, "ser");
    serNodes.forEach(ser => {
      let seriesName = "";
      const txNode = qLocal(ser, "tx");
      if (txNode) {
        const strRef = qLocal(txNode, "strRef");
        if (strRef) {
          const strCache = qLocal(strRef, "strCache");
          const vNode = strCache ? qLocal(strCache, "v") : null;
          if (vNode) seriesName = vNode.textContent.trim();
          if (!seriesName) {
            const fNode = qLocal(strRef, "f");
            if (fNode) seriesName = fNode.textContent.trim().replace(/^.*!/, "").replace(/\$/g, "");
          }
        }
        if (!seriesName) {
          const directV = qDirectChild(txNode, "v");
          if (directV) seriesName = directV.textContent.trim();
        }
        if (!seriesName) {
          const richNode = qLocal(txNode, "rich");
          if (richNode) {
            const txTexts = qLocalAll(richNode, "t");
            if (txTexts.length > 0) seriesName = txTexts.map(t => t.textContent).join("").trim();
          }
        }
      }
      if (!seriesName) {
        const orderNode = qLocal(ser, "order");
        const orderVal = orderNode ? orderNode.getAttribute("val") : null;
        seriesName = "Series" + (orderVal != null ? parseInt(orderVal, 10) + 1 : "");
      }

      const valNode  = qLocal(ser, "val") || qLocal(ser, "yVal");
      const numCache = valNode ? (qLocal(valNode, "numCache") || valNode) : null;
      const ptNodes  = numCache ? qLocalAll(numCache, "pt") : [];
      const vals = ptNodes.map(pt => {
        const v = qLocal(pt, "v");
        return v ? parseFloat(v.textContent) : 0;
      }).filter(v => !isNaN(v));

      // Parse xVal for scatter/bubble charts
      let xVals = null;
      const xValNode = qLocal(ser, "xVal");
      if (xValNode) {
        const xNumCache = qLocal(xValNode, "numCache") || qLocal(xValNode, "strCache") || xValNode;
        const xPtNodes = qLocalAll(xNumCache, "pt");
        xVals = xPtNodes.map(pt => {
          const v = qLocal(pt, "v");
          return v ? parseFloat(v.textContent) : 0;
        });
      }

      // Parse bubbleSize for bubble charts
      let bubbleSizes = null;
      const bubbleSizeNode = qLocal(ser, "bubbleSize");
      if (bubbleSizeNode) {
        const bCache = qLocal(bubbleSizeNode, "numCache") || bubbleSizeNode;
        const bPtNodes = qLocalAll(bCache, "pt");
        bubbleSizes = bPtNodes.map(pt => {
          const v = qLocal(pt, "v");
          return v ? parseFloat(v.textContent) : 0;
        });
      }

      if (vals.length > 0) {
        const seriesObj = { name: seriesName, data: vals, renderType: group.renderType };
        if (xVals && xVals.length > 0) seriesObj.xData = xVals;
        if (bubbleSizes && bubbleSizes.length > 0) seriesObj.bubbleSizes = bubbleSizes;
        result.series.push(seriesObj);
      }
    });
    serNodes.forEach(s => allSerNodes.push(s));
  }

  for (const ser of allSerNodes) {
    const catNode = qLocal(ser, "cat");
    if (!catNode) continue;
    const cache = qLocal(catNode, "strCache") || qLocal(catNode, "numCache");
    if (cache) {
      const catPts = qLocalAll(cache, "pt");
      result.categories = catPts.map(pt => {
        const v = qLocal(pt, "v");
        return v ? v.textContent : "";
      });
      if (result.categories.length > 0) break;
    }
  }

  return result;
}

async function parseExtendedChart(chartDoc, zip, ctx) {
  const result = { title: "", chartType: "bar", isCombo: false, series: [], categories: [] };

  const titleNode = qLocal(chartDoc, "title");
  if (titleNode) {
    const vNode = qLocal(titleNode, "v");
    if (vNode) result.title = (vNode.textContent || "").trim();
    if (!result.title) {
      const tNodes = qLocalAll(titleNode, "t");
      if (tNodes.length > 0) result.title = tNodes.map(t => t.textContent || "").join("").trim();
    }
  }

  const series = qLocal(chartDoc, "series");
  if (!series) {
    if (!result.title) result.title = "Chart";
    return result;
  }

  const layoutId = series.getAttribute("layoutId") || "";
  result.chartType = CHART_EX_LAYOUT_MAP[layoutId] || "bar";
  result.layoutId = layoutId;

  let seriesName = "";
  const txNode = qLocal(series, "tx");
  if (txNode) {
    const vNode = qLocal(txNode, "v");
    if (vNode) seriesName = (vNode.textContent || "").trim();
    if (!seriesName) {
      const fNode = qLocal(txNode, "f");
      if (fNode) {
        const fText = fNode.textContent.trim();
        const formula = ctx.definedNames[fText] || fText;
        const vals = await readRangeValues(zip, ctx, formula);
        if (vals.length > 0) seriesName = vals[0];
      }
    }
  }
  if (!seriesName) seriesName = "Series 1";

  const chartData = qLocal(chartDoc, "chartData");
  let categories = [];
  let values = [];

  if (chartData) {
    const dataBlocks = qLocalAll(chartData, "data");
    for (const dataBlock of dataBlocks) {
      const dims = qLocalAll(dataBlock, "strDim").concat(qLocalAll(dataBlock, "numDim"));
      for (const dim of dims) {
        const type = (dim.getAttribute("type") || "").toLowerCase();
        const fNode = qLocal(dim, "f");
        if (!fNode) continue;
        const ref = (fNode.textContent || "").trim();
        const cells = await readRangeValues(zip, ctx, ref);
        if (type === "cat") {
          categories = cells;
        } else if (type === "val" || type === "size" || type === "colorval" || type === "y") {
          values = cells.map(v => {
            const n = parseFloat(v);
            return isNaN(n) ? 0 : n;
          });
        }
      }
    }
  }

  if (values.length > 0) {
    result.series.push({ name: seriesName, data: values, renderType: result.chartType });
  }
  result.categories = categories;
  if (!result.title) result.title = seriesName || "Chart";

  return result;
}

export async function extractChartsFromZip(zip) {
  const chartsBySheet = {};
  try {
    const ctx = await loadWorkbookContext(zip);
    if (!Object.keys(ctx.sheetIdToName).length) return chartsBySheet;

    for (const sheetRel of Object.keys(ctx.sheetPathToName)) {
      const sheetName = ctx.sheetPathToName[sheetRel];
      const parts = sheetRel.split("/");
      const fname = parts.pop();
      const sheetRelsPath = ("xl/" + parts.join("/") + "/_rels/" + fname + ".rels").replace(/\/\//g, "/");

      const sheetRelsFile = zip.file(sheetRelsPath);
      if (!sheetRelsFile) continue;

      const relsXml = await sheetRelsFile.async("string");
      const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");

      const drawingPaths = [];
      qLocalAll(relsDoc, "Relationship").forEach(rel => {
        const type = rel.getAttribute("Type") || "";
        if (type.indexOf("/relationships/drawing") !== -1) {
          const target = rel.getAttribute("Target");
          const resolved = resolveZipPath(sheetRelsPath, target);
          if (resolved) drawingPaths.push(resolved);
        }
      });

      if (!chartsBySheet[sheetName]) chartsBySheet[sheetName] = [];
      let chartIdx = 0;

      for (const drawingPath of drawingPaths) {
        const drawingFile = zip.file(drawingPath);
        if (!drawingFile) continue;

        const dParts = drawingPath.split("/");
        const dFname = dParts.pop();
        const drawingRelsPath = dParts.join("/") + "/_rels/" + dFname + ".rels";

        const drawingRelsMap = {};
        const drawingRelsFile = zip.file(drawingRelsPath);
        if (drawingRelsFile) {
          const dRelsXml = await drawingRelsFile.async("string");
          const dRelsDoc = new DOMParser().parseFromString(dRelsXml, "application/xml");
          qLocalAll(dRelsDoc, "Relationship").forEach(rel => {
            const id = rel.getAttribute("Id");
            const target = rel.getAttribute("Target");
            const resolved = resolveZipPath(drawingRelsPath, target);
            if (id && resolved) drawingRelsMap[id] = resolved;
          });
        }

        const drawingXml = await drawingFile.async("string");
        const drawingDoc = new DOMParser().parseFromString(drawingXml, "application/xml");

        const allAnchors = [];
        ANCHOR_NAMES.forEach(name => {
          qLocalAll(drawingDoc, name).forEach(node => allAnchors.push({ name: name, node: node }));
        });

        for (const item of allAnchors) {
          const anchor = item.node;
          const found = extractAnchorChart(anchor);
          if (!found) continue;
          const chartPath = drawingRelsMap[found.ref.rId];
          if (!chartPath) continue;

          chartsBySheet[sheetName].push({
            id: "chart-" + sheetName + "-" + (chartIdx++),
            type: "chart",
            chartPath: chartPath,
            isExtended: !!found.ref.isExtended,
            range: found.range,
          });
        }
      }
    }

    for (const sheetName in chartsBySheet) {
      for (const chartObj of chartsBySheet[sheetName]) {
        const chartFile = zip.file(chartObj.chartPath);
        if (!chartFile) { chartObj.placeholder = true; continue; }

        const chartXml = await chartFile.async("string");
        const chartDoc = new DOMParser().parseFromString(chartXml, "application/xml");

        let parsed = null;
        try {
          parsed = chartObj.isExtended
            ? await parseExtendedChart(chartDoc, zip, ctx)
            : parseStandardChart(chartDoc);
        } catch (err) {
          console.warn("[chartParser] Failed to parse chart", chartObj.chartPath, err);
        }

        if (!parsed) { chartObj.placeholder = true; continue; }

        chartObj.title       = parsed.title || "Chart";
        chartObj.chartType   = parsed.chartType || "bar";
        chartObj.isCombo     = !!parsed.isCombo;
        chartObj.series      = parsed.series || [];
        chartObj.categories  = parsed.categories || [];
        chartObj.layoutId    = parsed.layoutId;
        chartObj.placeholder = chartObj.series.length === 0;

        console.log(
          "[extractChartsFromZip] Parsed chart:",
          chartObj.id,
          "type:", chartObj.chartType,
          "extended:", chartObj.isExtended,
          "series:", chartObj.series.length,
          "title:", chartObj.title,
        );
      }
    }
  } catch (err) {
    console.warn("Custom chart extraction failed", err);
  }
  return chartsBySheet;
}
